import { captureRemixErrorBoundaryError, withSentry } from '@sentry/remix'
import type { MetaFunction, LinksFunction, LoaderFunctionArgs, HeadersFunction } from '@remix-run/node'
import type { Theme } from '#app/utils/hooks/use-theme'
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
} from '@remix-run/react'
import { json } from '@remix-run/node'
import { useChangeLanguage } from 'remix-i18next/react'
import { AuthenticityTokenProvider } from 'remix-utils/csrf/react'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { authenticator } from '#app/modules/auth/auth.server'
import { useNonce } from '#app/utils/hooks/use-nonce'
import { getHints } from '#app/utils/hooks/use-hints'
import { getTheme, useTheme } from '#app/utils/hooks/use-theme'
import { getToastSession } from '#app/utils/toast.server'
import { csrf } from '#app/utils/csrf.server'
import { honeypot } from '#app/utils/honeypot.server'
import { combineHeaders, getDomainUrl } from '#app/utils/misc.server'
import { siteConfig } from '#app/utils/constants/brand'
import { useToast } from '#app/components/toaster'
import { Toaster } from '#app/components/ui/sonner'
import { ClientHintCheck } from '#app/components/misc/client-hints'
import { GenericErrorBoundary } from '#app/components/misc/error-boundary'
import i18nServer, { localeCookie } from '#app/modules/i18n/i18n.server'
import '@fontsource/poppins/index.css'
import '@fontsource/poppins/100.css'
import '@fontsource/poppins/200.css'
import '@fontsource/poppins/300.css'
import '@fontsource/poppins/400.css'
import '@fontsource/poppins/700.css'
import '@fontsource/poppins/800.css'
import '@fontsource/poppins/900.css'
import quillSnowStyles from 'quill/dist/quill.snow.css?url'
import quillBubbleStyles from 'quill/dist/quill.bubble.css?url'

import RootCSS from './root.css?url'
import { api } from '#convex/_generated/api.js'
import { convexHttpClient } from './utils/convexContext'
import { ConvexProvider, ConvexReactClient } from 'convex/react'
import { useEffect, useState } from 'react'
import { invariant } from '@epic-web/invariant'

export const handle = { i18n: ['translation'] }

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
    { title: data ? `${siteConfig.siteTitle}` : `Error | ${siteConfig.siteTitle}` },
    {
      name: 'description',
      content: siteConfig.siteDescription,
    }
  ]
}

export const links: LinksFunction = () => {
  return [
    { rel: 'icon', type: 'image/x-icon', href: siteConfig.favicon },
    { rel: 'stylesheet', href: RootCSS },
    { rel: 'stylesheet', href: quillSnowStyles },
    { rel: 'stylesheet', href: quillBubbleStyles },
    {
      rel: 'preload',
      as: 'font',
      href: '/fonts/micrenc.ttf',
      type: 'font/ttf',
      crossOrigin: 'anonymous',
    },
  ]
}

export async function loader({ request }: LoaderFunctionArgs) {
  const sessionUser = await authenticator.isAuthenticated(request)
  const CONVEX_URL = process.env['CONVEX_URL']
  invariant(CONVEX_URL, 'CONVEX_URL is not set')
  const user = sessionUser?._id
    ? await convexHttpClient.query(api.user.getById, { id: sessionUser?._id })
    : null

  const locale = await i18nServer.getLocale(request)
  const { toast, headers: toastHeaders } = await getToastSession(request)
  const [csrfToken, csrfCookieHeader] = await csrf.commitToken()

  return json(
    {
      user,
      locale,
      toast,
      csrfToken,
      honeypotProps: honeypot.getInputProps(),
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
        userPrefs: { theme: getTheme(request) },
      },
      ENV: { CONVEX_URL },
    } as const,
    {
      headers: combineHeaders(
        { 'Set-Cookie': await localeCookie.serialize(locale) },
        toastHeaders,
        csrfCookieHeader ? { 'Set-Cookie': csrfCookieHeader } : null,
      ),
    },
  )
}

function Document({
  children,
  nonce,
  lang = 'en',
  dir = 'ltr',
  theme = 'light',
  ENV,
}: {
  children: React.ReactNode
  nonce: string
  lang?: string
  dir?: 'ltr' | 'rtl'
  theme?: Theme
  ENV: { CONVEX_URL: string }
}) {
  const [convexClient] = useState(new ConvexReactClient(ENV.CONVEX_URL))
  
  return (
    <html
      lang={lang}
      dir={dir}
      className={`${theme} overflow-x-hidden`}
      style={{ colorScheme: theme }}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <ClientHintCheck nonce={nonce} />
        <Meta />
        <Links />
      </head>
      <body className="h-auto w-full font-sans">
        <ConvexProvider client={convexClient}>{children}</ConvexProvider>
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
        <Toaster closeButton position="bottom-center" theme={theme} />
      </body>
    </html>
  )
}

function AppWithProviders() {
  const { locale, toast, csrfToken, honeypotProps, ENV } = useLoaderData<typeof loader>()

  const nonce = useNonce()
  // WE CAN USE THIS IN THE FUTURE TO ALLOW THE USER TO ALLOW DARK MODE
  // const theme = useTheme()
  const theme = 'light'
  // Updates the i18n instance language.
  useChangeLanguage(locale)

  // Renders toast (if any).
  useToast(toast)

  return (
    <Document nonce={nonce} theme={theme} lang={locale ?? 'en'} ENV={ENV}>
      <AuthenticityTokenProvider token={csrfToken}>
        <HoneypotProvider {...honeypotProps}>
          <Outlet />
        </HoneypotProvider>
      </AuthenticityTokenProvider>
    </Document>
  )
}

export default withSentry(AppWithProviders)

export function ErrorBoundary() {
  const error = useRouteError()
  const nonce = useNonce()
  const theme = useTheme()
  const { ENV } = useLoaderData<typeof loader>()
  captureRemixErrorBoundaryError(error)
  return (
    <Document nonce={nonce} theme={theme} ENV={ENV}>
      <GenericErrorBoundary
        statusHandlers={{
          403: ({ error }) => (
            <p>Something went wrong: {error?.data.message}</p>
          ),
        }}
      />
    </Document>
  )
}
